import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

type AIMode = 'auto' | 'suggestions' | 'manual';

interface ConversationAIMode {
  conversationId: string;
  mode: AIMode;
  needsAttention: boolean;
}

export function useConversationAIModes(conversationIds: string[]) {
  const [modes, setModes] = useState<Map<string, ConversationAIMode>>(new Map());
  const [loading, setLoading] = useState(false);

  const fetchConversationModes = useCallback(async () => {
    if (conversationIds.length === 0) return;
    
    // Debounce to prevent rapid successive calls
    setLoading(true);
    try {
      const { data: tenantSchema } = await supabase.rpc('get_user_tenant_schema');
      if (!tenantSchema) return;

      // Fetch AI modes for all conversations - BATCH QUERY (single call)
      const { data: modesData, error } = await supabase.rpc('get_conversation_ai_modes_batch', {
        p_tenant_schema: tenantSchema,
        p_conversation_ids: conversationIds
      });

      if (error) {
        console.error('Error fetching conversation AI modes:', error);
        // Return all conversations with 'manual' mode on error
        const modeResults = conversationIds.map(id => ({
          conversationId: id,
          mode: 'manual' as AIMode
        }));
        setModes(new Map(modeResults.map(({ conversationId, mode }) => [
          conversationId,
          { conversationId, mode, needsAttention: false }
        ])));
        return;
      }

      // Transform batch RPC response to expected format
      const modeResults = modesData?.map(row => ({
        conversationId: row.conversation_id,
        mode: (row.agent_mode || 'manual') as AIMode
      })) || [];

      // Handle conversations that weren't in the result (defensive programming)
      const returnedIds = new Set(modeResults.map(r => r.conversationId));
      const missingConvs = conversationIds
        .filter(id => !returnedIds.has(id))
        .map(id => ({ conversationId: id, mode: 'manual' as AIMode }));

      const allModeResults = [...modeResults, ...missingConvs];
      
      const newModes = new Map<string, ConversationAIMode>();
      allModeResults.forEach(({ conversationId, mode }) => {
        newModes.set(conversationId, {
          conversationId,
          mode,
          needsAttention: false // Will be calculated based on unread messages and mode
        });
      });

      setModes(newModes);
    } catch (error) {
      console.error('Error fetching conversation AI modes:', error);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(conversationIds)]);

  const updateConversationMode = useCallback(async (conversationId: string, newMode: AIMode) => {
    try {
      const { data: tenantSchema } = await supabase.rpc('get_user_tenant_schema');
      if (!tenantSchema) return false;

      const { error } = await supabase.rpc('set_conversation_ai_mode', {
        tenant_schema: tenantSchema,
        conversation_id_param: conversationId,
        new_mode: newMode
      });

      if (error) {
        console.error(`Failed to update AI mode for conversation ${conversationId}:`, error);
        return false;
      }

      // Update local state
      setModes(prev => {
        const newModes = new Map(prev);
        const existing = newModes.get(conversationId);
        if (existing) {
          newModes.set(conversationId, { ...existing, mode: newMode });
        }
        return newModes;
      });

      return true;
    } catch (error) {
      console.error('Error updating conversation AI mode:', error);
      return false;
    }
  }, []);

  const updateAttentionState = useCallback((conversationId: string, needsAttention: boolean) => {
    setModes(prev => {
      const newModes = new Map(prev);
      const existing = newModes.get(conversationId);
      if (existing) {
        newModes.set(conversationId, { ...existing, needsAttention });
      }
      return newModes;
    });
  }, []);

  const setAllToMode = useCallback(async (mode: AIMode) => {
    const updatePromises = conversationIds.map(id => updateConversationMode(id, mode));
    const results = await Promise.all(updatePromises);
    return results.every(result => result);
  }, [conversationIds, updateConversationMode]);

  useEffect(() => {
    fetchConversationModes();
  }, [fetchConversationModes]);

  return {
    modes,
    loading,
    updateConversationMode,
    updateAttentionState,
    setAllToMode,
    refreshModes: fetchConversationModes
  };
}